import React, { useEffect, useMemo, useState } from 'react';
import { X, Calendar as CalendarIcon, Check, Trash2, Edit2, Save, Link as LinkIcon, Users as UsersIcon, Cpu, AlertTriangle, Video, MapPin, User as UserIcon, Clock, EyeOff } from 'lucide-react';
import EntityLink from '../shared/EntityLink';
import { useScheduledEvents } from '../../hooks/useScheduledEvents';
import EventTypeBadge, { EVENT_TYPE_OPTIONS } from './EventTypeBadge';
import { colaboradores } from '../../utils/stageHelpers';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import PostMeetingSection from './PostMeetingSection';
import EntitySearchField from './EntitySearchField';
import { Plus } from 'lucide-react';

const STATUS_META = {
  draft:            { label: 'Rascunho',           color: 'bg-slate-100 text-slate-700' },
  pending_approval: { label: 'Pendente aprovação', color: 'bg-amber-100 text-amber-700' },
  scheduled:        { label: 'Agendado',           color: 'bg-blue-100 text-blue-700' },
  completed:        { label: 'Concluído',          color: 'bg-emerald-100 text-emerald-700' },
  cancelled:        { label: 'Cancelado',          color: 'bg-slate-100 text-slate-500' },
  overdue:          { label: 'Atrasado',           color: 'bg-red-100 text-red-700' },
};

const PRIORITY_OPTIONS = [
  { value: 'low',      label: 'Baixa' },
  { value: 'medium',   label: 'Média' },
  { value: 'high',     label: 'Alta' },
  { value: 'critical', label: 'Crítica' },
];

const collaboratorOptions = colaboradores.filter((c) => c.value !== 'all');
const ENTITY_LINK_TYPES = new Set(['empresa', 'contato', 'imovel', 'oferta']);

function formatDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return value;
  }
}

function toLocalInput(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 16);
}

function fromLocalInput(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function colaboradorLabel(id) {
  if (!id) return '—';
  const c = collaboratorOptions.find((x) => x.value === id);
  return c ? c.label : id;
}

export default function EventDetailModal({ open, onClose, eventRef, onChanged }) {
  const { updateEvent, cancelEvent, completeEvent, deleteEventPermanently, fetchEventDetails, saving } = useScheduledEvents();
  const { user } = useAuth();

  const isDerived = eventRef?.sourceTable && eventRef.sourceTable !== 'scheduled_events';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);
  const [links, setLinks] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [externalDetails, setExternalDetails] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRemoveExternal, setConfirmRemoveExternal] = useState(false);
  const [removingExternal, setRemovingExternal] = useState(false);

  const [form, setForm] = useState({});

  // Onda 5.5 — nomes resolvidos dos links + form de vinculação posterior
  const [linkLabels, setLinkLabels] = useState({}); // key `${entity_type}:${entity_id}` -> label string
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkPick, setLinkPick] = useState({ empresa: null, oferta: null, contato: null });
  const [linkSaving, setLinkSaving] = useState(false);

  const isExternal = isDerived && eventRef?.sourceTable === 'external_calendar_events';

  useEffect(() => {
    if (!open || !eventRef?.sourceId) return;
    if (isDerived) {
      setEvent(null);
      setLinks([]);
      setAttendees([]);
      // Para eventos externos, busca a linha completa da tabela para garantir
      // attendees/organizer/meet_url frescos (a view pode estar atrasada na cache local).
      if (isExternal) {
        let cancelled = false;
        setLoading(true);
        setError(null);
        setExternalDetails(null);
        setConfirmRemoveExternal(false);
        (async () => {
          const { data, error: fetchErr } = await supabase
            .from('external_calendar_events')
            .select('id, summary, description, location, start_at, end_at, all_day, status, attendees, meet_url, organizer, colaborador_id')
            .eq('id', eventRef.sourceId)
            .maybeSingle();
          if (cancelled) return;
          if (fetchErr) {
            setError(fetchErr.message);
          } else {
            setExternalDetails(data || null);
          }
          setLoading(false);
        })();
        return () => { cancelled = true; };
      }
      setExternalDetails(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEditMode(false);
    setConfirmCancel(false);
    setConfirmDelete(false);
    (async () => {
      const res = await fetchEventDetails(eventRef.sourceId);
      if (cancelled) return;
      if (res.error) {
        setError(res.error);
      } else {
        setEvent(res.event);
        setLinks(res.links);
        setAttendees(res.attendees);
        setForm({
          title: res.event.title_internal || '',
          description: res.event.description_internal || '',
          eventType: res.event.event_type,
          priority: res.event.priority,
          allDay: res.event.all_day,
          startAt: toLocalInput(res.event.start_at),
          endAt: toLocalInput(res.event.end_at),
          ownerColaboradorId: res.event.owner_colaborador_id || '',
          assignedToColaboradorId: res.event.assigned_to_colaborador_id || '',
        });
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, eventRef, isDerived, isExternal, fetchEventDetails]);

  const statusMeta = useMemo(() => {
    const status = isDerived ? eventRef?.status : event?.status;
    return STATUS_META[status] || { label: status || '—', color: 'bg-slate-100 text-slate-700' };
  }, [event, eventRef, isDerived]);

  // Onda 5.5 — enriquecer links com nomes reais das entidades
  useEffect(() => {
    if (!open || !links || links.length === 0) {
      setLinkLabels({});
      return;
    }
    let cancelled = false;
    (async () => {
      const next = {};
      const byType = { empresa: [], oferta: [], contato: [] };
      for (const l of links) {
        if (byType[l.entity_type]) byType[l.entity_type].push(l.entity_id);
      }
      const tasks = [];
      if (byType.empresa.length) {
        tasks.push(
          supabase.from('empresas').select('id, empresa, company_name').in('id', byType.empresa)
            .then(({ data }) => (data || []).forEach((e) => {
              next[`empresa:${e.id}`] = e.company_name || e.empresa || e.id;
            }))
        );
      }
      if (byType.oferta.length) {
        tasks.push(
          supabase.from('ofertas').select('id, nome').in('id', byType.oferta)
            .then(({ data }) => (data || []).forEach((o) => {
              next[`oferta:${o.id}`] = o.nome || o.id;
            }))
        );
      }
      if (byType.contato.length) {
        tasks.push(
          supabase.from('contatos').select('id, first_name, last_name, email').in('id', byType.contato)
            .then(({ data }) => (data || []).forEach((c) => {
              const nm = `${c.first_name || ''} ${c.last_name || ''}`.trim();
              next[`contato:${c.id}`] = nm || c.email || c.id;
            }))
        );
      }
      await Promise.all(tasks);
      if (!cancelled) setLinkLabels(next);
    })();
    return () => { cancelled = true; };
  }, [open, links]);

  // Onda 5.5+ — auto-suggest empresa/contato a partir do título/descrição
  // quando o usuário abre o mini-form de vínculo e o evento ainda não tem links.
  useEffect(() => {
    if (!showLinkForm || links.length > 0 || !event) return;

    const text = `${event.title_internal || ''} ${event.description_internal || ''}`;

    // 1) Empresa: palavras em CAIXA ALTA com 3+ letras (excluindo stopwords)
    const SKIP_UPPER = new Set([
      'REUNIÃO', 'REUNIAO', 'REUNIÕES', 'TESTE', 'TESTES', 'SOBRE', 'COM',
      'PARA', 'AGENDAR', 'CALL', 'FOLLOW', 'FOLLOWUP', 'VISITA', 'NOVO',
      'NOVA', 'DEMO', 'CRM', 'REIP', 'ASTON', 'EQUIPE', 'INTERNO', 'INTERNA',
    ]);
    const upperMatches = text.match(/\b[A-ZÁÉÍÓÚÀÂÃÊÔÇ]{3,}\b/g) || [];
    const candidates = Array.from(new Set(upperMatches.filter((w) => !SKIP_UPPER.has(w))));

    // 2) Contato: pares de palavras capitalizadas (\"João Silva\", \"Maria Eduarda\")
    const nameRegex = /\b([A-ZÁÉÍÓÚÀÂÃÊÔÇ][a-záéíóúàâãêôç]+)\s+([A-ZÁÉÍÓÚÀÂÃÊÔÇ][a-záéíóúàâãêôç]+)\b/g;
    const nameCandidates = [];
    const seenNames = new Set();
    let m;
    while ((m = nameRegex.exec(text)) !== null) {
      const full = `${m[1]} ${m[2]}`;
      if (!seenNames.has(full)) {
        seenNames.add(full);
        nameCandidates.push({ first: m[1], last: m[2], full });
      }
    }

    if (candidates.length === 0 && nameCandidates.length === 0) return;
    let cancelled = false;

    (async () => {
      // Tentar casar empresa pelo primeiro candidato em CAIXA ALTA
      if (candidates.length > 0) {
        const term = candidates[0];
        const { data } = await supabase
          .from('empresas')
          .select('id, empresa, company_name')
          .or(`empresa.ilike.%${term}%,company_name.ilike.%${term}%`)
          .limit(3);
        if (!cancelled && data && data.length === 1) {
          const row = data[0];
          setLinkPick((prev) => prev.empresa ? prev : ({
            ...prev,
            empresa: {
              type: 'empresa',
              id: row.id,
              label: row.company_name || row.empresa || row.id,
              sub: null,
              raw: row,
            },
          }));
        }
      }

      // Tentar casar contato pelo primeiro par capitalizado
      if (nameCandidates.length > 0) {
        const { first, last, full } = nameCandidates[0];
        const { data } = await supabase
          .from('contatos')
          .select('id, first_name, last_name, email')
          .or(`and(first_name.ilike.${first},last_name.ilike.${last}),and(first_name.ilike.${last},last_name.ilike.${first})`)
          .limit(3);
        if (!cancelled && data && data.length === 1) {
          const row = data[0];
          const nm = `${row.first_name || ''} ${row.last_name || ''}`.trim() || full;
          setLinkPick((prev) => prev.contato ? prev : ({
            ...prev,
            contato: {
              type: 'contato',
              id: row.id,
              label: nm,
              sub: row.email || null,
              raw: row,
            },
          }));
        }
      }
    })();

    return () => { cancelled = true; };
  }, [showLinkForm, links.length, event]);

  if (!open) return null;

  const handleSaveEdit = async () => {
    setError(null);
    if (!form.title?.trim()) {
      setError('Título é obrigatório');
      return;
    }
    const startISO = fromLocalInput(form.startAt);
    if (!startISO) {
      setError('Data/hora de início inválida');
      return;
    }
    const endISO = fromLocalInput(form.endAt);
    if (endISO && new Date(endISO) < new Date(startISO)) {
      setError('Fim não pode ser antes do início');
      return;
    }
    const { error: updErr } = await updateEvent(event.id, {
      title: form.title,
      description: form.description,
      eventType: form.eventType,
      priority: form.priority,
      allDay: form.allDay,
      startAt: startISO,
      endAt: endISO,
      ownerColaboradorId: form.ownerColaboradorId,
      assignedToColaboradorId: form.assignedToColaboradorId,
    });
    if (updErr) {
      setError(updErr);
      return;
    }
    setEditMode(false);
    onChanged?.();
    onClose?.();
  };

  const handleComplete = async () => {
    setError(null);
    const { error: err } = await completeEvent(event.id);
    if (err) { setError(err); return; }
    onChanged?.();
    onClose?.();
  };

  const handleCancel = async () => {
    setError(null);
    const { error: err } = await cancelEvent(event.id);
    if (err) { setError(err); return; }
    onChanged?.();
    onClose?.();
  };

  const handleDeletePermanent = async () => {
    setError(null);
    const { error: err } = await deleteEventPermanently(event.id);
    if (err) { setError(err); return; }
    onChanged?.();
    onClose?.();
  };

  const handleRemoveExternal = async () => {
    if (!eventRef?.sourceId) return;
    setError(null);
    setRemovingExternal(true);
    const { error: delErr } = await supabase
      .from('external_calendar_events')
      .delete()
      .eq('id', eventRef.sourceId);
    setRemovingExternal(false);
    if (delErr) { setError(delErr.message); return; }
    onChanged?.();
    onClose?.();
  };

  // Onda 5.5 — vincular entidade(s) ao evento já existente
  const handleAddLinks = async () => {
    if (!event?.id) return;
    const rows = [];
    if (linkPick.empresa) rows.push({ scheduled_event_id: event.id, entity_type: 'empresa', entity_id: linkPick.empresa.id, relation_type: 'subject' });
    if (linkPick.oferta)  rows.push({ scheduled_event_id: event.id, entity_type: 'oferta',  entity_id: linkPick.oferta.id,  relation_type: 'subject' });
    if (linkPick.contato) rows.push({ scheduled_event_id: event.id, entity_type: 'contato', entity_id: linkPick.contato.id, relation_type: 'participant' });

    // Dedup contra links existentes
    const novos = rows.filter((r) => !links.some((l) => l.entity_type === r.entity_type && l.entity_id === r.entity_id));
    if (novos.length === 0) {
      setShowLinkForm(false);
      setLinkPick({ empresa: null, oferta: null, contato: null });
      return;
    }
    setLinkSaving(true);
    setError(null);
    const { error: insErr } = await supabase.from('scheduled_event_links').insert(novos);
    setLinkSaving(false);
    if (insErr) {
      setError(`Falha ao vincular: ${insErr.message}`);
      return;
    }
    // Refetch dos detalhes para atualizar `links`
    const fresh = await fetchEventDetails(event.id);
    if (!fresh.error) {
      setLinks(fresh.links);
      setAttendees(fresh.attendees);
    }
    setShowLinkForm(false);
    setLinkPick({ empresa: null, oferta: null, contato: null });
    onChanged?.();
  };

  const inputClass =
    'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <CalendarIcon className="text-blue-600 flex-shrink-0" size={20} />
            <h2 className="text-lg font-semibold text-slate-800 truncate">
              {isDerived ? eventRef?.title : (event?.title_internal || 'Carregando...')}
            </h2>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${statusMeta.color}`}>
              {statusMeta.label}
            </span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
            <X size={22} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{error}</div>
          )}

          {isExternal && (() => {
            // Prefere os dados frescos da tabela; cai em eventRef se ainda carregando
            const d = externalDetails || {};
            const startAt = d.start_at ?? eventRef.startAt;
            const endAt = d.end_at ?? eventRef.endAt;
            const location = d.location ?? eventRef.location;
            const meetUrl = d.meet_url ?? eventRef.meetUrl;
            const organizer = d.organizer ?? eventRef.organizer;
            const description = d.description ?? eventRef.description;
            const attendeesArr = Array.isArray(d.attendees)
              ? d.attendees
              : (Array.isArray(eventRef.attendees) ? eventRef.attendees : []);
            const colabColor = eventRef.colaboradorColor;

            return (
              <>
                {colabColor && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: colabColor.bgLight || `${colabColor.bg}1F`,
                        color: colabColor.bg,
                        border: `1px solid ${colabColor.bg}`,
                      }}
                    >
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                        style={{ backgroundColor: colabColor.bg, color: colabColor.text }}
                      >
                        {colabColor.initial}
                      </span>
                      {colabColor.name}
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-start gap-2">
                    <Clock size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-slate-500 uppercase">Início</div>
                      <div className="text-sm text-slate-700">{formatDateTime(startAt)}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Clock size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-slate-500 uppercase">Fim</div>
                      <div className="text-sm text-slate-700">{formatDateTime(endAt)}</div>
                    </div>
                  </div>
                </div>

                {location && (
                  <div className="flex items-start gap-2">
                    <MapPin size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs text-slate-500 uppercase">Local</div>
                      <div className="text-sm text-slate-700 whitespace-pre-wrap break-words">{location}</div>
                    </div>
                  </div>
                )}

                {meetUrl && (
                  <a
                    href={meetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 hover:bg-emerald-100 text-sm font-medium w-fit"
                  >
                    <Video size={16} /> Entrar com Google Meet
                  </a>
                )}

                {attendeesArr.length > 0 && (
                  <div className="flex items-start gap-2">
                    <UsersIcon size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-500 uppercase">Participantes ({attendeesArr.length})</div>
                      <ul className="text-sm space-y-1 mt-1">
                        {attendeesArr.map((att, i) => {
                          const status = (att?.status || '').toUpperCase();
                          const dotClass =
                            status === 'ACCEPTED' ? 'bg-emerald-500'
                            : status === 'DECLINED' ? 'bg-red-500'
                            : status === 'TENTATIVE' ? 'bg-amber-500'
                            : 'bg-slate-300';
                          return (
                            <li key={`${att?.email || i}-${i}`} className="flex items-center gap-2 text-slate-700">
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`} />
                              <span className="truncate">{att?.name || att?.email || '—'}</span>
                              {att?.email && att?.name && (
                                <span className="text-xs text-slate-400 truncate">{att.email}</span>
                              )}
                              {status && (
                                <span className="text-[10px] text-slate-400 uppercase tracking-wide flex-shrink-0">{status}</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}

                {description && (
                  <div>
                    <div className="text-xs text-slate-500 uppercase mb-1">Descrição</div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap break-words bg-slate-50 border border-slate-200 rounded p-2 max-h-48 overflow-y-auto">
                      {description}
                    </div>
                  </div>
                )}

                {organizer && (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <UserIcon size={12} /> Organizado por <span className="text-slate-700">{organizer}</span>
                  </div>
                )}

                <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-2 flex items-center gap-1">
                  <span>🌐</span>
                  <span>
                    Calendário externo · importado de Google Calendar / Outlook.
                    Para editar, altere no calendário de origem e clique em <em>Sincronizar</em>.
                  </span>
                </div>
              </>
            );
          })()}
          {isDerived && eventRef.sourceTable !== 'external_calendar_events' && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                <strong>Item derivado.</strong> Este evento é gerado automaticamente a partir de
                {' '}<code className="bg-amber-100 px-1 rounded">{eventRef.sourceTable}</code> e não pode ser editado aqui.
                Para alterar, abra a entidade de origem no menu Ofertas.
              </div>
            </div>
          )}

          {loading && <div className="text-sm text-slate-500">Carregando...</div>}

          {!isDerived && event && !editMode && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Tipo</div>
                  <EventTypeBadge type={event.event_type} />
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Prioridade</div>
                  <div className="text-sm text-slate-700 capitalize">{event.priority}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Início</div>
                  <div className="text-sm text-slate-700">{formatDateTime(event.start_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Fim</div>
                  <div className="text-sm text-slate-700">{formatDateTime(event.end_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Responsável</div>
                  <div className="text-sm text-slate-700">{colaboradorLabel(event.owner_colaborador_id)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Atribuído a</div>
                  <div className="text-sm text-slate-700">{colaboradorLabel(event.assigned_to_colaborador_id)}</div>
                </div>
              </div>

              {event.description_internal && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Descrição</div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{event.description_internal}</div>
                </div>
              )}

              {event.created_by_type === 'agent' && (
                <div className="bg-teal-50 border border-teal-200 rounded p-2 text-xs text-teal-800 flex items-center gap-1.5">
                  <Cpu size={12} /> Criado pelo agent <strong>{event.created_by_agent || 'desconhecido'}</strong>
                  {event.requires_approval && ' — pendente de aprovação humana'}
                </div>
              )}

              {event.meet_url && (
                <div className="bg-sky-50 border border-sky-200 rounded p-2 text-xs text-sky-800 flex items-center gap-1.5">
                  <Video size={12} />
                  <a href={event.meet_url} target="_blank" rel="noreferrer" className="underline">{event.meet_url}</a>
                </div>
              )}

              {/* Onda 5.5 — Contexto REIP (sempre visível para REIP) */}
              <div className="border-t border-slate-200 pt-3">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 flex items-center gap-1">
                  <LinkIcon size={12} /> Contexto REIP
                </div>
                {links.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {links.map((l) => {
                      const label = linkLabels[`${l.entity_type}:${l.entity_id}`] || l.entity_id?.slice(0, 8) || '—';
                      const isClickable = ENTITY_LINK_TYPES.has(l.entity_type);
                      return (
                        <div key={l.id || `${l.entity_type}:${l.entity_id}`} className="text-xs px-2 py-1 bg-slate-100 rounded flex items-center gap-1">
                          <span className="text-slate-500">{l.entity_type}:</span>
                          {isClickable ? (
                            <EntityLink entityType={l.entity_type} entityId={l.entity_id} label={label} />
                          ) : (
                            <code className="text-slate-700">{label}</code>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 mb-2">Nenhum vínculo com entidade do REIP.</div>
                )}

                {!showLinkForm ? (
                  <button
                    onClick={() => setShowLinkForm(true)}
                    className="text-xs px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 flex items-center gap-1"
                  >
                    <Plus size={12} /> {links.length > 0 ? 'Vincular mais' : 'Vincular empresa/oferta/contato'}
                  </button>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2">
                    <EntitySearchField entityType="empresa" value={linkPick.empresa} onChange={(v) => setLinkPick((p) => ({ ...p, empresa: v }))} />
                    <EntitySearchField entityType="oferta"  value={linkPick.oferta}  onChange={(v) => setLinkPick((p) => ({ ...p, oferta:  v }))} />
                    <EntitySearchField entityType="contato" value={linkPick.contato} onChange={(v) => setLinkPick((p) => ({ ...p, contato: v }))} />
                    <div className="flex items-center justify-end gap-2 pt-1">
                      <button
                        onClick={() => { setShowLinkForm(false); setLinkPick({ empresa: null, oferta: null, contato: null }); }}
                        className="text-xs px-2 py-1 text-slate-600 hover:bg-slate-200 rounded"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleAddLinks}
                        disabled={linkSaving || (!linkPick.empresa && !linkPick.oferta && !linkPick.contato)}
                        className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {linkSaving ? 'Vinculando...' : 'Vincular'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {attendees.length > 0 && (
                <div>
                  <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><UsersIcon size={12} /> Participantes</div>
                  <ul className="text-sm text-slate-700 space-y-0.5">
                    {attendees.map((a) => (
                      <li key={a.id} className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">{a.attendee_type === 'internal' ? '🏠' : '🌐'}</span>
                        <span>{a.attendee_type === 'internal' ? colaboradorLabel(a.colaborador_id) : (a.name || a.email || '—')}</span>
                        {a.role && a.role !== 'attendee' && (
                          <span className="text-xs text-slate-500">({a.role})</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(() => {
                const startMs = event?.start_at ? Date.parse(event.start_at) : NaN;
                const endMs = event?.end_at ? Date.parse(event.end_at) : startMs;
                const isPast = Number.isFinite(endMs) && endMs < Date.now();
                const isCompleted = event?.status === 'completed';
                if (!isPast && !isCompleted) return null;
                return (
                  <PostMeetingSection
                    event={event}
                    links={links}
                    currentUserEmail={user?.email}
                    onChanged={onChanged}
                  />
                );
              })()}
            </>
          )}

          {!isDerived && event && editMode && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Título</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className={inputClass}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tipo</label>
                  <select
                    value={form.eventType}
                    onChange={(e) => setForm((f) => ({ ...f, eventType: e.target.value }))}
                    className={inputClass}
                  >
                    {EVENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prioridade</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                    className={inputClass}
                  >
                    {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="edit-allday"
                  type="checkbox"
                  checked={!!form.allDay}
                  onChange={(e) => setForm((f) => ({ ...f, allDay: e.target.checked }))}
                  className="rounded border-slate-300"
                />
                <label htmlFor="edit-allday" className="text-sm text-slate-700">Dia inteiro</label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Início</label>
                  <input
                    type={form.allDay ? 'date' : 'datetime-local'}
                    value={form.allDay ? (form.startAt || '').slice(0, 10) : (form.startAt || '')}
                    onChange={(e) => setForm((f) => ({ ...f, startAt: f.allDay ? `${e.target.value}T00:00` : e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fim</label>
                  <input
                    type={form.allDay ? 'date' : 'datetime-local'}
                    value={form.allDay ? (form.endAt ? form.endAt.slice(0, 10) : '') : (form.endAt || '')}
                    onChange={(e) => setForm((f) => ({ ...f, endAt: f.allDay && e.target.value ? `${e.target.value}T23:59` : e.target.value }))}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Responsável</label>
                  <select
                    value={form.ownerColaboradorId || ''}
                    onChange={(e) => setForm((f) => ({ ...f, ownerColaboradorId: e.target.value }))}
                    className={inputClass}
                  >
                    <option value="">—</option>
                    {collaboratorOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Atribuído a</label>
                  <select
                    value={form.assignedToColaboradorId || ''}
                    onChange={(e) => setForm((f) => ({ ...f, assignedToColaboradorId: e.target.value }))}
                    className={inputClass}
                  >
                    <option value="">—</option>
                    {collaboratorOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
                <textarea
                  value={form.description || ''}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className={`${inputClass} resize-none`}
                />
              </div>
            </>
          )}
        </div>

        {!isDerived && event && (
          <div className="p-4 border-t border-slate-200 bg-slate-50 flex flex-wrap justify-end gap-2">
            {!editMode && event.status !== 'completed' && event.status !== 'cancelled' && (
              <>
                <button
                  onClick={handleComplete}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Check size={14} /> Concluir
                </button>
                {!confirmCancel ? (
                  <button
                    onClick={() => setConfirmCancel(true)}
                    disabled={saving}
                    className="px-3 py-1.5 text-sm bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <Trash2 size={14} /> Cancelar evento
                  </button>
                ) : (
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <Trash2 size={14} /> Confirmar cancelamento
                  </button>
                )}
                <button
                  onClick={() => setEditMode(true)}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Edit2 size={14} /> Editar
                </button>
              </>
            )}
            {!editMode && (
              !confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm bg-white border border-red-400 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Trash2 size={14} /> Excluir definitivamente
                </button>
              ) : (
                <button
                  onClick={handleDeletePermanent}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Trash2 size={14} /> Tem certeza? Excluir
                </button>
              )
            )}
            {editMode && (
              <>
                <button
                  onClick={() => setEditMode(false)}
                  className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 rounded-lg"
                >
                  Cancelar edição
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Save size={14} /> {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </>
            )}
          </div>
        )}

        {isDerived && !isExternal && (
          <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Fechar
            </button>
          </div>
        )}

        {isExternal && (
          <div className="p-4 border-t border-slate-200 bg-slate-50 space-y-2">
            {confirmRemoveExternal && (
              <div className="text-xs text-slate-600 bg-slate-100 border border-slate-200 rounded p-2">
                Este evento será removido do REIP mas continuará no seu Google Calendar/Outlook.
                Na próxima sincronização, ele pode reaparecer.
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              {!confirmRemoveExternal ? (
                <button
                  onClick={() => setConfirmRemoveExternal(true)}
                  disabled={removingExternal}
                  className="px-3 py-1.5 text-sm bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <EyeOff size={14} /> Remover do REIP
                </button>
              ) : (
                <button
                  onClick={handleRemoveExternal}
                  disabled={removingExternal}
                  className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <EyeOff size={14} /> {removingExternal ? 'Removendo...' : 'Tem certeza? Remover'}
                </button>
              )}
              <button onClick={onClose} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Fechar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
